import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import "./scheduler.css";
import "./common-view.css";
import ClientSearch from "./client-search";
import useAuthSession from "../hooks/useAuthSession";
import { BACKEND_BASE_URL } from "../config/appUrls";
import { hasValidPlatformAccess } from "../utils/auth";

function safeParsePg(ts) {
  if (!ts) return null;
  const isoLike = String(ts).trim().replace(" ", "T");
  const d = new Date(isoLike);
  return isNaN(d.getTime()) ? null : d;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function toPgTimestamp(d) {
  if (!(d instanceof Date) || isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const hh = pad2(d.getHours());
  const mi = pad2(d.getMinutes());
  const ss = pad2(d.getSeconds());
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
}

function pgToDatetimeLocal(pg) {
  if (!pg) return "";
  const d = safeParsePg(pg);
  if (!d) return "";
  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const hh = pad2(d.getHours());
  const mi = pad2(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function datetimeLocalToPg(v) {
  if (!v) return "";
  const s = String(v).trim();
  return s.includes("T") ? `${s.replace("T", " ")}:00` : s;
}

const EVENTS_PAGE_SIZE = 5;

export default function Scheduler() {
  const { t, i18n } = useTranslation();
  const auth = useAuthSession();
  const { token, clientId, language } = auth;
  const [selectedClient, setSelectedClient] = useState(null);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [targetClientId, setTargetClientId] = useState(clientId || "");
  const [events, setEvents] = useState([]);
  const [statusMsg, setStatusMsg] = useState("");
  const [view, setView] = useState("month");
  const [uploading, setUploading] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [savingEvent, setSavingEvent] = useState(false);
  const [accountText, setAccountText] = useState("");
  const [eventPage, setEventPage] = useState(0);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [deletingEvent, setDeletingEvent] = useState(false);
  const [anchorDate, setAnchorDate] = useState(() => new Date());

  const currentLocale = useMemo(() => {
    const lang = (i18n.language || language || "en").toLowerCase();
    return lang.startsWith("es") ? "es-ES" : "en-US";
  }, [i18n.language, language]);

  const statusLabel = status => t(`scheduler.${status}`, status);

  const [editDraft, setEditDraft] = useState({
    title: "",
    description: "",
    status: "confirmed",
    location: null,
    start_at: "",
    end_at: "",
    timezone: null,
    metadata: {},
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    event_type: "",
    outcome: "",
    notes: "",
    account: {}
  });

  const paginatedEvents = events.slice(
    eventPage * EVENTS_PAGE_SIZE,
    (eventPage + 1) * EVENTS_PAGE_SIZE
  );
  const hasNextEventPage = (eventPage + 1) * EVENTS_PAGE_SIZE < events.length;
  const hasPrevEventPage = eventPage > 0;

  const handleClientSelect = useCallback(client => {
    setSelectedClient(client);
    setTargetClientId(client.client_id);
  }, []);

  const hasPlatformAccess = useMemo(() => {
    return hasValidPlatformAccess(selectedClient);
  }, [selectedClient]);

  useEffect(() => {
    if (!selectedClient) return;
    if (!hasPlatformAccess) {
      alert(t("dashboard.subscriptionRequiredDesc"));
      window.location.href = "/dashboard?tab=onboarding";
    }
  }, [hasPlatformAccess, selectedClient, t]);

  function formatDateRange(start, end) {
    const s = safeParsePg(start);
    const e = safeParsePg(end);
    if (!s || !e) return "";

    return `${s.toLocaleDateString(currentLocale)} · ${s.toLocaleTimeString(currentLocale, {
      hour: "2-digit",
      minute: "2-digit"
    })} – ${e.toLocaleTimeString(currentLocale, {
      hour: "2-digit",
      minute: "2-digit"
    })}`;
  }

  function getRange(view, anchor) {
    const base = anchor instanceof Date && !isNaN(anchor.getTime()) ? new Date(anchor) : new Date();
    const start = new Date(base);
    const end = new Date(base);

    if (view === "day") {
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
    } else if (view === "week") {
      const day = (base.getDay() + 6) % 7;
      start.setDate(base.getDate() - day);
      start.setHours(0, 0, 0, 0);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
    } else if (view === "month") {
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      end.setMonth(start.getMonth() + 1, 0);
      end.setHours(23, 59, 59, 999);
    } else if (view === "year") {
      start.setMonth(0, 1);
      start.setHours(0, 0, 0, 0);
      end.setMonth(11, 31);
      end.setHours(23, 59, 59, 999);
    }
    return { from: toPgTimestamp(start), to: toPgTimestamp(end) };
  }

  async function loadEvents({ token, clientId }) {
    try {
      setErr("");
      setLoading(true);
      const { from, to } = getRange(view, anchorDate);

      const res = await fetch(`${BACKEND_BASE_URL}/api/schedule/events/search`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ client_id: clientId, from, to })
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || "Failed to load schedule events");

      setEvents(Array.isArray(json.events) ? json.events : []);
      setEventPage(0);
    } catch (e) {
      console.error("Failed to load schedule:", e);
      setErr(e.message || "Failed to fetch");
    } finally {
      setLoading(false);
    }
  }

  function capitalizeFirst(s) {
    if (!s) return s;
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  function shiftAnchor(direction) {
    setAnchorDate(prev => {
      const d = prev instanceof Date && !isNaN(prev.getTime()) ? new Date(prev) : new Date();
      if (view === "day") {
        d.setDate(d.getDate() + direction);
      } else if (view === "week") {
        d.setDate(d.getDate() + direction * 7);
      } else if (view === "month") {
        const dayOfMonth = d.getDate();
        d.setDate(1);
        d.setMonth(d.getMonth() + direction);
        const maxDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
        d.setDate(Math.min(dayOfMonth, maxDay));
      } else if (view === "year") {
        d.setFullYear(d.getFullYear() + direction);
      }
      return d;
    });
  }

  useEffect(() => {
    if (!token || !targetClientId) return;
    loadEvents({ token, clientId: targetClientId });
  }, [view, anchorDate, targetClientId]);

  function formatAnchorLabel(view, anchorDate, i18n) {
    if (!anchorDate) return "";
    const locale = i18n.language || "en";
    const d = new Date(anchorDate);

    if (view === "month") {
      return capitalizeFirst(d.toLocaleDateString(locale, { month: "long", year: "numeric" }));
    }
    if (view === "year") {
      return d.getFullYear().toString();
    }
    if (view === "week") {
      const start = new Date(d);
      start.setDate(d.getDate() - ((d.getDay() + 6) % 7));
      const end = new Date(start);
      end.setDate(start.getDate() + 6);

      if (locale.startsWith("es")) {
        const monthName = capitalizeFirst(start.toLocaleDateString(locale, { month: "long" }));
        return `Del ${start.getDate()} al ${end.getDate()} de ${monthName} de ${start.getFullYear()}`;
      }
      const monthName = capitalizeFirst(start.toLocaleDateString(locale, { month: "long" }));
      return `${monthName} ${start.getDate()} – ${end.getDate()}, ${start.getFullYear()}`;
    }

    if (locale.startsWith("es")) {
      const parts = new Intl.DateTimeFormat(locale, { day: "numeric", month: "long", year: "numeric" }).formatToParts(d);
      return parts.map(p => (p.type === "month" ? capitalizeFirst(p.value) : p.value)).join("");
    }
    return capitalizeFirst(d.toLocaleDateString(locale, { month: "long", day: "numeric", year: "numeric" }));
  }

  async function uploadScheduleFile(file) {
    if (!file) return;

    const form = new FormData();
    form.append("file", file);
    const effectiveClientId = selectedClient?.client_id || targetClientId;

    if (!effectiveClientId) {
      setErr(t("commonLabel.selectClientFirst", "Please select a client first."));
      return;
    }

    form.append("client_id", effectiveClientId);

    try {
      setUploading(true);
      setErr("");
      setStatusMsg("");

      const res = await fetch(`${BACKEND_BASE_URL}/api/schedule/import`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Upload failed");

      await loadEvents({ token, clientId: effectiveClientId });
      setStatusMsg(`${json.inserted} events`);
    } catch (e) {
      setStatusMsg(e.message || "Import finished with no events");
    } finally {
      setUploading(false);
    }
  }

  function startEditEvent(ev) {
    setEditingEventId(ev.event_id);
    const acc = ev.account && Object.keys(ev.account).length > 0 ? JSON.stringify(ev.account, null, 2) : "";
    setAccountText(acc);
    setEditDraft({
      title: ev.title || "",
      description: ev.description || "",
      status: ev.status || "confirmed",
      location: ev.location ?? null,
      start_at: ev.start_at,
      end_at: ev.end_at ?? "",
      timezone: ev.timezone ?? null,
      metadata: ev.metadata || {},
      contact_name: ev.contact_name || "",
      contact_phone: ev.contact_phone || "",
      contact_email: ev.contact_email || "",
      event_type: ev.event_type || "",
      outcome: ev.outcome || "",
      notes: ev.notes || "",
      account: ev.account || {}
    });
  }

  function startCreateEvent() {
    setEditingEventId("__new__");
    setAccountText("");
    const now = new Date();
    setEditDraft({
      title: "",
      description: "",
      status: "confirmed",
      location: "",
      start_at: toPgTimestamp(now),
      end_at: toPgTimestamp(new Date(now.getTime() + 30 * 60 * 1000)),
      timezone: null,
      metadata: {},
      contact_name: "",
      contact_phone: "",
      contact_email: "",
      event_type: "",
      outcome: "",
      notes: "",
      account: {}
    });
  }

  function cancelEditEvent() {
    setEditingEventId(null);
    setAccountText("");
    setEditDraft({ title: "", description: "", status: "confirmed" });
  }

  async function saveEditEvent(ev) {
    if (!token) return;
    try {
      setSavingEvent(true);
      setErr("");
      const effectiveClientId = selectedClient?.client_id || targetClientId;

      if (!effectiveClientId) {
        setErr("Client not resolved for event save");
        return;
      }

      const isNew = !ev?.event_id || editingEventId === "__new__";
      const payload = {
        client_id: effectiveClientId,
        ...(isNew ? {} : { event_id: ev.event_id }),
        title: editDraft.title,
        description: editDraft.description,
        status: editDraft.status,
        start_at: editDraft.start_at,
        end_at: editDraft.end_at ?? null,
        timezone: editDraft.timezone ?? null,
        location: editDraft.location ?? null,
        metadata: editDraft.metadata || {},
        contact_name: editDraft.contact_name,
        contact_phone: editDraft.contact_phone || null,
        contact_email: editDraft.contact_email || null,
        event_type: editDraft.event_type || null,
        outcome: editDraft.outcome || null,
        notes: editDraft.notes || null,
        account: editDraft.account || {}
      };

      const res = await fetch(`${BACKEND_BASE_URL}/api/schedule/events`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || "Failed to save event");

      const saved = json.event;
      if (isNew) {
        setEvents(prev => [saved, ...prev]);
        setEventPage(0);
      } else {
        setEvents(prev => prev.map(e => (e.event_id === saved.event_id ? saved : e)));
      }
      cancelEditEvent();
    } catch (e) {
      setErr(e.message || "Failed to save event");
    } finally {
      setSavingEvent(false);
    }
  }

  async function confirmDeleteEvent() {
    if (!eventToDelete) return;
    setDeletingEvent(true);

    try {
      const effectiveClientId = selectedClient?.client_id || targetClientId;
      const res = await fetch(`${BACKEND_BASE_URL}/api/schedule/events`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          event_id: eventToDelete.event_id,
          client_id: effectiveClientId
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Delete failed");
      }

      setEvents(prev => prev.filter(ev => ev.event_id !== eventToDelete.event_id));
      setEventToDelete(null);
    } catch (err) {
      console.error("Delete event failed:", err);
      alert(err.message);
    } finally {
      setDeletingEvent(false);
    }
  }

  return (
    <div className="view-page">
      <div className="view-card">
        <div className="view-header">
          <div>
            <h1 className="view-title">{t("scheduler.title")}</h1>
            <p className="view-subtitle">{t("scheduler.subtitle")}</p>
            {statusMsg && <div className="info-box" style={{ marginTop: 10 }}>✅ {statusMsg}</div>}
          </div>

          <div className="view-nav">
            <select
              className="view-dropdown"
              defaultValue=""
              onChange={e => {
                const route = e.target.value;
                if (route) window.location.href = route;
              }}
            >
              <option value="" disabled>{t("dropDown.scheduler-page")}</option>
              <option value="/">{t("dropDown.home-page")}</option>
              <option value="/support">{t("dropDown.support-page")}</option>
              <option value="/clients">{t("dropDown.clients-page")}</option>
              <option value="/dashboard">{t("dropDown.dashboard-page")}</option>
              <option value="/crm-lite">{t("dropDown.crm-lite-page")}</option>
              <option value="/qr-code">{t("dropDown.qr-page")}</option>
              <option value="/commerce">{t("dropDown.commerce-page")}</option>
              <option value="/dispatcher">{t("dropDown.dispatcher-page")}</option>
              <option value="/analytics">{t("dropDown.analytics-page")}</option>
              <option value="/tutorial">{t("dropDown.tutorial-page")}</option>
            </select>
          </div>
        </div>

        <ClientSearch onSelect={handleClientSelect} />

        <div className="sch-view-controls">
          <button className="btn-link sch-nav-btn" onClick={() => shiftAnchor(-1)} aria-label={t("commonLabel.previous")}>
            <span className="sch-arrow">
              <svg width="40" height="60" viewBox="0 0 24 24" fill="currentColor"><path d="M15 18l-6-6 6-6" /></svg>
            </span>
          </button>

          <div style={{ display: "flex", gap: 8 }}>
            {["day", "week", "month", "year"].map(v => (
              <button
                key={v}
                className={`btn-secondary ${view === v ? "active" : ""}`}
                onClick={() => {
                  setView(v);
                  setAnchorDate(new Date());
                }}
              >
                {t(`scheduler.views.${v}`)}
              </button>
            ))}
          </div>

          <button className="btn-link sch-nav-btn" onClick={() => shiftAnchor(1)} aria-label={t("commonLabel.next")}>
            <span className="sch-arrow">
              <svg width="40" height="60" viewBox="0 0 24 24" fill="currentColor"><path d="M9 6l6 6-6 6" /></svg>
            </span>
          </button>

          <div className="sch-anchor-label">{formatAnchorLabel(view, anchorDate, i18n)}</div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label className="btn-secondary" style={{ cursor: "pointer" }}>
            {uploading ? t("scheduler.uploading") : t("scheduler.upload")}
            <input
              type="file"
              accept=".xlsx,.xls,.csv,.ics"
              hidden
              disabled={uploading}
              onChange={e => uploadScheduleFile(e.target.files[0])}
            />
          </label>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
            {t("scheduler.supportedFormats")}
          </div>
        </div>

        <div className="sch-tools">
          <div className="sch-tools-header">
            <div className="sch-tools-title">{t("scheduler.events")}</div>
            <button
              className="btn-link sch-add-event"
              onClick={startCreateEvent}
              disabled={savingEvent}
              title={t("scheduler.addEvent")}
            >
              {t("scheduler.addEvent")}
            </button>
          </div>

          {loading ? (
            <div>{t("commonLabel.loading")}</div>
          ) : (
            <div>
              <div style={{ display: "grid", gap: 12 }}>
                {editingEventId === "__new__" && (
                  <div className="event-row">
                    <div className="event-card editing" onClick={e => e.stopPropagation()}>
                      <div className="event-title">✏️ {t("scheduler.evtTitle")}</div>

                      <div style={{ marginTop: 10 }}>
                        <div className="event-label">{t("scheduler.evtSubTitle")}</div>
                        <input
                          className="event-input"
                          value={editDraft.title}
                          placeholder={t("scheduler.evtSubTitle")}
                          onChange={e => setEditDraft(d => ({ ...d, title: e.target.value }))}
                        />
                      </div>

                      <div style={{ marginTop: 10 }}>
                        <div className="event-label">{t("scheduler.location")}</div>
                        <input
                          className="event-input"
                          placeholder={t("scheduler.location")}
                          value={editDraft.location || ""}
                          onChange={e => setEditDraft(d => ({ ...d, location: e.target.value }))}
                        />
                      </div>

                      <div style={{ marginTop: 10 }}>
                        <div className="event-label">{t("scheduler.startTime")}</div>
                        <input
                          type="datetime-local"
                          className="event-input"
                          value={pgToDatetimeLocal(editDraft.start_at)}
                          onChange={e => setEditDraft(d => ({ ...d, start_at: datetimeLocalToPg(e.target.value) }))}
                        />
                      </div>

                      <div style={{ marginTop: 10 }}>
                        <div className="event-label">{t("scheduler.endTime")}</div>
                        <input
                          type="datetime-local"
                          className="event-input"
                          value={pgToDatetimeLocal(editDraft.end_at)}
                          onChange={e => setEditDraft(d => ({ ...d, end_at: datetimeLocalToPg(e.target.value) }))}
                        />
                      </div>

                      <div style={{ marginTop: 10 }}>
                        <div className="event-label">{t("scheduler.description")}</div>
                        <textarea
                          className="event-textarea"
                          value={editDraft.description}
                          placeholder={t("scheduler.description")}
                          onChange={e => setEditDraft(d => ({ ...d, description: e.target.value }))}
                        />
                      </div>

                      <div style={{ marginTop: 10 }}>
                        <div className="event-label">{t("commonLabel.status")}</div>
                        <select
                          className="event-select"
                          value={editDraft.status}
                          onChange={e => setEditDraft(d => ({ ...d, status: e.target.value }))}
                        >
                          <option value="confirmed">{t("scheduler.confirmed")}</option>
                          <option value="tentative">{t("scheduler.tentative")}</option>
                          <option value="cancelled">{t("commonLabel.cancelled")}</option>
                        </select>
                      </div>

                      <div style={{ marginTop: 10 }}>
                        <div className="event-label">{t("scheduler.contactName")}</div>
                        <input
                          className="event-input"
                          placeholder={t("scheduler.contactName")}
                          value={editDraft.contact_name}
                          onChange={e => setEditDraft(d => ({ ...d, contact_name: e.target.value }))}
                        />
                      </div>

                      <div style={{ marginTop: 10 }}>
                        <div className="event-label">{t("scheduler.contactPhone")}</div>
                        <input
                          className="event-input"
                          placeholder={t("scheduler.contactPhone")}
                          value={editDraft.contact_phone}
                          onChange={e => setEditDraft(d => ({ ...d, contact_phone: e.target.value }))}
                        />
                      </div>

                      <div style={{ marginTop: 10 }}>
                        <div className="event-label">{t("scheduler.contactEmail")}</div>
                        <input
                          className="event-input"
                          placeholder={t("scheduler.contactEmail")}
                          value={editDraft.contact_email}
                          onChange={e => setEditDraft(d => ({ ...d, contact_email: e.target.value }))}
                        />
                      </div>

                      <div style={{ marginTop: 10 }}>
                        <div className="event-label">{t("scheduler.eventType")}</div>
                        <input
                          className="event-input"
                          placeholder={t("scheduler.eventType")}
                          value={editDraft.event_type}
                          onChange={e => setEditDraft(d => ({ ...d, event_type: e.target.value }))}
                        />
                      </div>

                      <div style={{ marginTop: 10 }}>
                        <div className="event-label">{t("scheduler.outcome")}</div>
                        <input
                          className="event-input"
                          placeholder={t("scheduler.outcome")}
                          value={editDraft.outcome}
                          onChange={e => setEditDraft(d => ({ ...d, outcome: e.target.value }))}
                        />
                      </div>

                      <div style={{ marginTop: 10 }}>
                        <div className="event-label">{t("scheduler.notes")}</div>
                        <textarea
                          className="event-textarea"
                          placeholder={t("scheduler.notes")}
                          value={editDraft.notes}
                          onChange={e => setEditDraft(d => ({ ...d, notes: e.target.value }))}
                        />
                      </div>

                      <div style={{ marginTop: 10 }}>
                        <div className="event-label">{t("scheduler.accountJson")}</div>
                        <textarea
                          className="event-textarea"
                          placeholder={t("scheduler.accountInfo")}
                          value={accountText}
                          onChange={e => {
                            const text = e.target.value;
                            setAccountText(text);
                            try {
                              const parsed = JSON.parse(text);
                              setEditDraft(d => ({ ...d, account: parsed }));
                            } catch {}
                          }}
                        />
                      </div>

                      <div className="event-edit-row">
                        <button className="btn-mini" onClick={cancelEditEvent}>
                          {t("commonLabel.cancel")}
                        </button>
                        <button className="btn-mini primary" disabled={savingEvent} onClick={() => saveEditEvent(null)}>
                          {savingEvent ? t("scheduler.saving") : t("commonLabel.save")}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {paginatedEvents.length === 0 ? (
                  <div className="empty-state">{t("scheduler.noEvents")}</div>
                ) : (
                  paginatedEvents.map(ev => {
                    const patient = ev.title || ev.metadata?.patient || "—";
                    const isEditing = editingEventId === ev.event_id;

                    return (
                      <div key={ev.event_id} className="event-row">
                        <div
                          className={`event-card ${isEditing ? "editing" : ""}`}
                          onClick={() => !isEditing && startEditEvent(ev)}
                        >
                          {!isEditing ? (
                            <>
                              <div className="event-tooltip">{t("scheduler.clickToEdit")}</div>
                              <div className="event-title">👤 {patient}</div>
                              <div className="event-time">🕒 {formatDateRange(ev.start_at, ev.end_at)}</div>
                              {ev.location && <div className="event-location">📍 {ev.location}</div>}
                              {ev.description && <div className="event-desc">🧾 {ev.description}</div>}

                              {(ev.contact_name || ev.contact_phone || ev.contact_email) && (
                                <div className="event-meta">
                                  📞 {ev.contact_name || ""} {ev.contact_phone ? `(${ev.contact_phone})` : ""} {ev.contact_email ? `— ${ev.contact_email}` : ""}
                                </div>
                              )}

                              {ev.event_type && (
                                <div className="event-meta">
                                  🏷️ {t("scheduler.type", "Type")}: {ev.event_type} {ev.outcome ? `| ${t("scheduler.outcome", "Outcome")}: ${ev.outcome}` : ""}
                                </div>
                              )}

                              {ev.notes && (
                                <div className="event-desc" style={{ fontSize: "0.9rem", color: "#4b5563" }}>
                                  📝 {ev.notes}
                                </div>
                              )}

                              <div className="event-status">🔖 {statusLabel(ev.status)}</div>
                            </>
                          ) : (
                            <>
                              <div className="event-title">{t("scheduler.evtTitle")}</div>

                              <div style={{ marginTop: 10 }}>
                                <div className="event-label">{t("scheduler.evtSubTitle")}</div>
                                <input
                                  className="event-input"
                                  value={editDraft.title}
                                  placeholder={t("scheduler.evtSubTitle")}
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => setEditDraft(d => ({ ...d, title: e.target.value }))}
                                />
                              </div>

                              <div style={{ marginTop: 10 }}>
                                <div className="event-label">{t("scheduler.location")}</div>
                                <input
                                  className="event-input"
                                  placeholder={t("scheduler.location")}
                                  value={editDraft.location || ""}
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => setEditDraft(d => ({ ...d, location: e.target.value }))}
                                />
                              </div>

                              <div style={{ marginTop: 10 }}>
                                <div className="event-label">{t("scheduler.startTime")}</div>
                                <input
                                  type="datetime-local"
                                  className="event-input"
                                  value={pgToDatetimeLocal(editDraft.start_at)}
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => setEditDraft(d => ({ ...d, start_at: datetimeLocalToPg(e.target.value) }))}
                                />
                              </div>

                              <div style={{ marginTop: 10 }}>
                                <div className="event-label">{t("scheduler.endTime")}</div>
                                <input
                                  type="datetime-local"
                                  className="event-input"
                                  value={pgToDatetimeLocal(editDraft.end_at)}
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => setEditDraft(d => ({ ...d, end_at: datetimeLocalToPg(e.target.value) }))}
                                />
                              </div>

                              <div style={{ marginTop: 10 }}>
                                <div className="event-label">{t("scheduler.description")}</div>
                                <textarea
                                  className="event-textarea"
                                  value={editDraft.description}
                                  placeholder={t("scheduler.description")}
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => setEditDraft(d => ({ ...d, description: e.target.value }))}
                                />
                              </div>

                              <div style={{ marginTop: 10 }}>
                                <div className="event-label">{t("commonLabel.status")}</div>
                                <select
                                  className="event-select"
                                  value={editDraft.status}
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => setEditDraft(d => ({ ...d, status: e.target.value }))}
                                >
                                  <option value="confirmed">{t("scheduler.confirmed")}</option>
                                  <option value="tentative">{t("scheduler.tentative")}</option>
                                  <option value="cancelled">{t("commonLabel.cancelled")}</option>
                                </select>
                              </div>

                              <div style={{ marginTop: 10 }}>
                                <div className="event-label">{t("scheduler.contactName")}</div>
                                <input
                                  className="event-input"
                                  placeholder={t("scheduler.contactName")}
                                  value={editDraft.contact_name}
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => setEditDraft(d => ({ ...d, contact_name: e.target.value }))}
                                />
                              </div>

                              <div style={{ marginTop: 10 }}>
                                <div className="event-label">{t("scheduler.contactPhone")}</div>
                                <input
                                  className="event-input"
                                  placeholder={t("scheduler.contactPhone")}
                                  value={editDraft.contact_phone}
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => setEditDraft(d => ({ ...d, contact_phone: e.target.value }))}
                                />
                              </div>

                              <div style={{ marginTop: 10 }}>
                                <div className="event-label">{t("scheduler.contactEmail")}</div>
                                <input
                                  className="event-input"
                                  placeholder={t("scheduler.contactEmail")}
                                  value={editDraft.contact_email}
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => setEditDraft(d => ({ ...d, contact_email: e.target.value }))}
                                />
                              </div>

                              <div style={{ marginTop: 10 }}>
                                <div className="event-label">{t("scheduler.eventType")}</div>
                                <input
                                  className="event-input"
                                  placeholder={t("scheduler.eventType")}
                                  value={editDraft.event_type}
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => setEditDraft(d => ({ ...d, event_type: e.target.value }))}
                                />
                              </div>

                              <div style={{ marginTop: 10 }}>
                                <div className="event-label">{t("scheduler.outcome")}</div>
                                <input
                                  className="event-input"
                                  placeholder={t("scheduler.outcome")}
                                  value={editDraft.outcome}
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => setEditDraft(d => ({ ...d, outcome: e.target.value }))}
                                />
                              </div>

                              <div style={{ marginTop: 10 }}>
                                <div className="event-label">{t("scheduler.notes")}</div>
                                <textarea
                                  className="event-textarea"
                                  placeholder={t("scheduler.notes")}
                                  value={editDraft.notes}
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => setEditDraft(d => ({ ...d, notes: e.target.value }))}
                                />
                              </div>

                              <div style={{ marginTop: 10 }}>
                                <div className="event-label">{t("scheduler.accountJson")}</div>
                                <textarea
                                  className="event-textarea"
                                  placeholder={t("scheduler.accountInfo")}
                                  value={accountText}
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => {
                                    const text = e.target.value;
                                    setAccountText(text);
                                    try {
                                      const parsed = JSON.parse(text);
                                      setEditDraft(d => ({ ...d, account: parsed }));
                                    } catch {}
                                  }}
                                />
                              </div>

                              <div className="event-edit-row" onClick={e => e.stopPropagation()}>
                                <button className="btn-mini" onClick={cancelEditEvent}>
                                  {t("commonLabel.cancel")}
                                </button>
                                <button className="btn-mini primary" disabled={savingEvent} onClick={() => saveEditEvent(ev)}>
                                  {savingEvent ? t("scheduler.saving") : t("commonLabel.save")}
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                        <button
                          className="event-delete-btn side"
                          title={t("commonLabel.delete")}
                          onClick={e => {
                            e.stopPropagation();
                            setEventToDelete(ev);
                          }}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })
                )}
              </div>

              {events.length > EVENTS_PAGE_SIZE && (
                <div className="common-pagination">
                  {hasPrevEventPage && (
                    <button className="btn-link" onClick={() => setEventPage(p => p - 1)}>
                      {t("commonLabel.previous")}
                    </button>
                  )}
                  {hasNextEventPage && (
                    <button className="btn-link" onClick={() => setEventPage(p => p + 1)}>
                      {t("commonLabel.next")}
                    </button>
                  )}
                </div>
              )}

              {eventToDelete && (
                <div className="confirm-overlay" onClick={() => setEventToDelete(null)}>
                  <div className="confirm-modal" onClick={e => e.stopPropagation()}>
                    <div className="confirm-title">{t("scheduler.confirmDeleteTitle")}</div>
                    <div className="confirm-text">
                      {t("scheduler.confirmDeleteMessage")} <strong>{eventToDelete.title}</strong>?
                    </div>
                    <div className="confirm-actions">
                      <button className="btn-mini" onClick={() => setEventToDelete(null)}>
                        {t("commonLabel.cancel")}
                      </button>
                      <button className="btn-mini primary" disabled={deletingEvent} onClick={confirmDeleteEvent}>
                        {deletingEvent ? t("scheduler.deleting") : t("commonLabel.delete")}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}